import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader } from "../../components/bits";
import TaskEditDialog, { accountableFor } from "../../components/TaskEditDialog";

const COLUMNS = [
  { key: "NotStarted", label: "Not Started", color: "#A3A3A3" },
  { key: "InProgress", label: "In Progress", color: "#1E6FE8" },
  { key: "Blocked", label: "Blocked", color: "#FF3B30" },
  { key: "Completed", label: "Completed", color: "#00C781" },
];

export default function KanbanPage() {
  const { projectId } = useParams();
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [over, setOver] = useState(null);
  const [groupBy, setGroupBy] = useState("none");
  const [editTask, setEditTask] = useState(null);

  const load = useCallback(() => {
    api.get(`/projects/${projectId}/schedule`).then((r) => setTasks((r.data.tasks || []).filter((t) => !t.isMilestone))).catch((e) => toast.error(errMsg(e)));
    api.get(`/projects/${projectId}/members`).then((r) => setMembers(r.data)).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const memberName = (t) => accountableFor(t, members);

  const lanes = (() => {
    if (groupBy === "none") return [{ key: "all", label: null, tasks }];
    if (groupBy === "owner") {
      const map = {};
      tasks.forEach((t) => {
        const k = memberName(t) || "Unassigned";
        (map[k] = map[k] || []).push(t);
      });
      return Object.entries(map).sort((a, b) => (a[0] === "Unassigned" ? 1 : b[0] === "Unassigned" ? -1 : a[0].localeCompare(b[0]))).map(([k, v]) => ({ key: k, label: k, tasks: v }));
    }
    const byId = Object.fromEntries(tasks.map((t) => [t.id, t]));
    const map = {};
    tasks.forEach((t) => {
      const parent = t.parentId ? byId[t.parentId] : null;
      const k = parent ? `${parent.wbsCode} ${parent.name}` : t.parentId ? "Other" : `${t.wbsCode} ${t.name}`;
      if (!t.parentId && tasks.some((x) => x.parentId === t.id)) return;
      (map[k] = map[k] || []).push(t);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0])).map(([k, v]) => ({ key: k, label: k, tasks: v }));
  })();

  const drop = async (e, status) => {
    e.preventDefault();
    setOver(null);
    const taskId = e.dataTransfer.getData("taskId");
    const task = tasks.find((t) => t.id === taskId);
    if (!task || task.status === status) return;
    const patch = { status };
    if (status === "Completed") patch.percentComplete = 100;
    if (status === "NotStarted") patch.percentComplete = 0;
    setTasks((ts) => ts.map((t) => (t.id === taskId ? { ...t, ...patch } : t)));
    try {
      await api.patch(`/projects/${projectId}/tasks/${taskId}`, patch);
      toast.success(`"${task.name}" → ${status}`);
    } catch (err) { toast.error(errMsg(err)); load(); }
  };

  return (
    <div data-testid="kanban-page">
      <PageHeader overline="Planning · Visual board" title="Kanban Board">
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs text-muted-foreground">Swimlanes:</span>
          {[["none", "None"], ["owner", "By Accountable"], ["phase", "By WBS Phase"]].map(([v, l]) => (
            <button key={v} data-testid={`groupby-${v}`} onClick={() => setGroupBy(v)}
              className={`text-xs uppercase tracking-wide border px-2 py-1 ${groupBy === v ? "border-[#1E6FE8] text-[#1E6FE8] bg-[#1E6FE8]/5 font-bold" : "border-border text-muted-foreground"}`}>{l}</button>
          ))}
        </div>
      </PageHeader>
      {lanes.map((lane) => (
        <div key={lane.key} className="mb-6" data-testid={lane.label ? "kanban-swimlane" : undefined}>
          {lane.label && (
            <div className="flex items-center gap-2 mb-2">
              <span className="font-display font-bold text-sm tracking-tight bg-black text-white px-2 py-0.5">{lane.label}</span>
              <span className="text-xs text-muted-foreground">{lane.tasks.length} tasks</span>
            </div>
          )}
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
            {COLUMNS.map((col) => {
              const todayStr = new Date().toISOString().slice(0, 10);
              const isLate = (t) => t.plannedFinish && t.plannedFinish < todayStr && (t.percentComplete || 0) < 100 && t.status !== "Completed";
              const colTasks = lane.tasks
                .filter((t) => (t.status || "NotStarted") === col.key)
                .sort((a, b) => {
                  const ra = isLate(a) ? 0 : a.plannedFinish ? 1 : 2;
                  const rb = isLate(b) ? 0 : b.plannedFinish ? 1 : 2;
                  if (ra !== rb) return ra - rb;
                  return String(a.plannedFinish || "9999").localeCompare(String(b.plannedFinish || "9999"));
                });
              const laneColKey = `${lane.key}:${col.key}`;
              return (
                <div key={col.key} data-testid={`kanban-col-${col.key}`}
                  onDragOver={(e) => { e.preventDefault(); setOver(laneColKey); }}
                  onDragLeave={() => setOver(null)}
                  onDrop={(e) => drop(e, col.key)}
                  className={`border bg-card flex flex-col ${lane.label ? "min-h-[140px]" : "min-h-[60vh]"} ${over === laneColKey ? "border-[#1E6FE8] bg-[#1E6FE8]/5" : "border-border"}`}>
                  <div className="px-3 py-2 border-b border-border flex items-center justify-between sticky top-0 bg-inherit">
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ color: col.color }}>{col.label}</span>
                    <span className="text-xs font-mono2 text-muted-foreground">{colTasks.length}</span>
                  </div>
                  <div className="p-2 space-y-2 overflow-y-auto flex-1">
                    {colTasks.map((t) => {
                      const acc = memberName(t);
                      const today = new Date().toISOString().slice(0, 10);
                      const overdue = t.plannedFinish && t.plannedFinish < today && (t.percentComplete || 0) < 100 && t.status !== "Completed";
                      return (
                        <div key={t.id} data-testid={overdue ? "kanban-card-overdue" : "kanban-card"} draggable
                          onDragStart={(e) => e.dataTransfer.setData("taskId", t.id)}
                          onClick={() => setEditTask(t)}
                          className={`border p-2.5 cursor-pointer hover:border-[#1E6FE8] ${overdue ? "bg-red-50 border-red-400 ring-1 ring-red-200" : "bg-white border-border"} ${t.computedCritical ? "border-l-4 border-l-[#FF3B30]" : ""}`}>
                          <div className="flex items-start justify-between gap-1">
                            <span className="text-sm font-medium leading-snug">{t.name}</span>
                            <span className="font-mono2 text-[10px] text-muted-foreground shrink-0">{t.wbsCode}</span>
                          </div>
                          <div className="flex items-center justify-between mt-1.5 text-[11px] text-muted-foreground">
                            <span className={overdue ? "text-red-600 font-bold" : ""}>
                              {t.plannedFinish ? `due ${t.plannedFinish}` : "—"}{overdue && <span data-testid="overdue-badge" className="ml-1 bg-red-600 text-white px-1 py-px text-[9px] uppercase tracking-wide">overdue</span>}
                            </span>
                            <span>{t.percentComplete || 0}%</span>
                          </div>
                          <div className="h-1 bg-neutral-100 mt-1.5">
                            <div className="h-1" style={{ width: `${t.percentComplete || 0}%`, background: col.color }} />
                          </div>
                          <div className="flex items-center gap-1.5 mt-1.5" data-testid="card-accountable">
                            <span className={`h-5 w-5 rounded-full text-[9px] font-bold flex items-center justify-center ${acc ? "bg-[#1E6FE8] text-white" : "bg-neutral-200 text-neutral-500"}`}>
                              {acc ? acc.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() : "?"}
                            </span>
                            <span className="text-[11px] text-muted-foreground truncate">{acc || "Unassigned"}</span>
                          </div>
                        </div>
                      );
                    })}
                    {colTasks.length === 0 && <div className="text-xs text-muted-foreground text-center py-4">Drop tasks here</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <TaskEditDialog projectId={projectId} task={editTask} members={members}
        open={!!editTask} onClose={() => setEditTask(null)} onSaved={load} />
    </div>
  );
}
